import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaBook, FaDownload, FaSync, FaCalendarAlt } from 'react-icons/fa';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_URL = isLocalhost ? 'http://localhost:5000/api' : 'https://fac.arsn.ci/api';

const LivreTresoreriePage = ({ user }) => {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
    const today = date.toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(firstDay);
    const [endDate, setEndDate] = useState(today);
    const [loadingData, setLoadingData] = useState(false);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [message, setMessage] = useState(null);
    const [livreData, setLivreData] = useState(null);

    // Couleurs administratives standards
    const COLORS = {
        success: '#198754',
        danger: '#dc3545',
        info: '#0dcaf0',
        warning: '#f59e0b'
    };

    const getToken = () => {
        const data = localStorage.getItem('facsmart_user');
        if (!data) return '';
        try {
            const parsed = JSON.parse(data);
            return parsed.token || parsed.user?.token || '';
        } catch (e) { return ''; }
    };

    const fetchLivreData = useCallback(async () => {
        setLoadingData(true);
        setMessage(null);
        try {
            const config = { headers: { Authorization: `Bearer ${getToken()}` } };
            const res = await axios.get(`${API_URL}/encaissements/livre-tresorerie/data?startDate=${startDate}&endDate=${endDate}`, config);
            setLivreData(res.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur lors de la récupération des données.' });
        } finally {
            setLoadingData(false);
        }
    }, [startDate, endDate]);

    useEffect(() => { fetchLivreData(); }, [fetchLivreData]);

    const handleDownloadPdf = async () => {
        setLoadingPdf(true);
        try {
            const config = { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' };
            const res = await axios.get(`${API_URL}/encaissements/livre-tresorerie?startDate=${startDate}&endDate=${endDate}`, config);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Livre_Tresorerie_${startDate}_au_${endDate}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur lors de la génération du PDF.' });
        } finally {
            setLoadingPdf(false);
        }
    };

    const formatMoney = (amount) => {
        if (amount === undefined || amount === null) return "";
        if (amount === 0) return "0";
        return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4 bg-light min-vh-100">
            
            {/* EN-TÊTE PROFESSIONNEL */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold mb-0 text-dark" style={{ letterSpacing: '0.5px' }}>
                        <FaBook className="me-2 text-primary" /> LIVRE DE TRÉSORERIE
                    </h4>
                    <p className="small text-muted mb-0">Suivi des flux financiers (Encaissements & Reversements)</p>
                </div>
                
                <button 
                    onClick={handleDownloadPdf} 
                    className="btn btn-primary shadow-sm fw-bold d-inline-flex align-items-center justify-content-center" 
                    disabled={loadingPdf || loadingData || !livreData}
                >
                    {loadingPdf ? <span className="spinner-border spinner-border-sm me-2" /> : <FaDownload className="me-2" />}
                    EXPORTER EN PDF
                </button>
            </div>

            {/* FILTRES PÉRIODE DANS UNE CARTE BLANCHE */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-3">
                    <form onSubmit={(e) => { e.preventDefault(); fetchLivreData(); }} className="row align-items-end g-3">
                        <div className="col-12 col-sm-5 col-md-4">
                            <label className="fw-bold small mb-1 text-muted text-uppercase">Du</label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white"><FaCalendarAlt className="text-muted"/></span>
                                <input type="date" className="form-control border-start-0" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </div>
                        </div>
                        <div className="col-12 col-sm-5 col-md-4">
                            <label className="fw-bold small mb-1 text-muted text-uppercase">Au</label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white"><FaCalendarAlt className="text-muted"/></span>
                                <input type="date" className="form-control border-start-0" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </div>
                        </div>
                        <div className="col-12 col-sm-2 col-md-4">
                            <button type="submit" className="btn btn-outline-primary btn-sm w-100 fw-bold shadow-sm" disabled={loadingData}>
                                <FaSync className={`me-2 ${loadingData ? 'fa-spin' : ''}`} /> ACTUALISER LES DONNÉES
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {message && (
                <div className={`alert ${message.type === 'error' ? 'alert-danger' : 'alert-success'} border-0 shadow-sm mb-4`}>
                    {message.text}
                </div>
            )}

            {/* TABLEAU DE DONNÉES ÉPURÉ */}
            <div className="card border-0 shadow-sm overflow-hidden">
                <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                    <table className="table table-hover table-sm mb-0 text-nowrap align-middle">
                        <thead className="bg-dark text-white sticky-top">
                            <tr className="text-center text-uppercase small" style={{ letterSpacing: '1px' }}>
                                <th className="py-3 border-0">Date</th>
                                <th className="py-3 border-0">Opération</th>
                                <th className="py-3 border-0">Référence</th>
                                <th className="py-3 border-0">Structure</th>
                                <th className="py-3 border-0">Facture</th>
                                <th className="py-3 border-0">Prestation</th>
                                <th className="py-3 border-0">Entrée (FCFA)</th>
                                <th className="py-3 border-0">Sortie (FCFA)</th>
                                <th className="py-3 border-0">Solde Caisse</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingData ? (
                                <tr>
                                    <td colSpan="9" className="text-center py-5">
                                        <div className="spinner-border text-primary spinner-border-sm me-2" /> 
                                        Traitement des données en cours...
                                    </td>
                                </tr>
                            ) : livreData ? (
                                <>
                                    {/* --- SOLDE INITIAL --- */}
                                    <tr className="table-warning border-top">
                                        <td colSpan="8" className="text-center py-3 fw-bold text-uppercase">
                                            Solde Antérieur reporté au {new Date(startDate).toLocaleDateString()}
                                        </td>
                                        <td className="text-end pe-3 fw-bold fs-6">
                                            {formatMoney(livreData.soldeAnterieurGlobal)}
                                        </td>
                                    </tr>

                                    {livreData.days.length === 0 ? (
                                        <tr><td colSpan="9" className="text-center py-5 text-muted">Aucun mouvement enregistré sur cette période.</td></tr>
                                    ) : (
                                        (() => {
                                            let currentRunningSolde = livreData.soldeAnterieurGlobal || 0;
                                            return livreData.days.map((day) => {
                                                let dayEnc = 0;
                                                let dayDec = 0;
                                                
                                                const transactionRows = day.transactions.map((t, tIdx) => {
                                                    if (t.type === 'encaissement') {
                                                        currentRunningSolde += t.amount;
                                                        dayEnc += t.amount;
                                                    } else {
                                                        currentRunningSolde -= t.amount;
                                                        dayDec += t.amount;
                                                    }
                                                    return (
                                                        <tr key={`t-${day.dateStr}-${tIdx}`} className="small fw-medium">
                                                            <td className="text-center py-2">{day.dateStr}</td>
                                                            <td className="ps-2 text-wrap" style={{minWidth: '200px'}}>{t.nature}</td>
                                                            <td className="text-center fw-bold">{t.reference}</td>
                                                            <td className="ps-2 text-wrap" style={{minWidth: '150px'}}>{t.structure}</td>
                                                            <td className="text-center">{t.facture}</td>
                                                            <td className="ps-2 text-wrap" style={{minWidth: '200px'}}>{t.prestation}</td>
                                                            <td className="text-end pe-2 fw-bold" style={{ color: COLORS.success }}>{t.type === 'encaissement' ? formatMoney(t.amount) : ''}</td>
                                                            <td className="text-end pe-2 fw-bold" style={{ color: COLORS.danger }}>{t.type === 'decaissement' ? formatMoney(t.amount) : ''}</td>
                                                            <td className="text-end pe-2 bg-light fw-bold" style={{ color: COLORS.info }}>{formatMoney(currentRunningSolde)}</td>
                                                        </tr>
                                                    );
                                                });

                                                return (
                                                    <React.Fragment key={`day-${day.dateStr}`}>
                                                        {transactionRows}
                                                        {/* BILAN QUOTIDIEN */}
                                                        <tr className="bg-light-subtle">
                                                            <td colSpan="6" className="text-end pe-3 py-2 fw-bold text-muted small">BILAN DU {day.dateStr}</td>
                                                            <td className="text-end pe-2 py-2 fw-bold border-top" style={{ color: COLORS.success }}>{formatMoney(dayEnc)}</td>
                                                            <td className="text-end pe-2 py-2 fw-bold border-top" style={{ color: COLORS.danger }}>{formatMoney(dayDec)}</td>
                                                            <td className="text-end pe-2 py-2 fw-bold border-top" style={{ color: COLORS.info }}>{formatMoney(currentRunningSolde)}</td>
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            });
                                        })()
                                    )}
                                </>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LivreTresoreriePage;